import { ApiError } from '@clockwise/shared';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ImportSheet } from '@/components/custom/import-sheet';
import { serviceRolesApi } from '@/lib/api';
import { triggerDownload } from '@/lib/download';
import { queryKeys } from '@/lib/query-keys';

interface ServiceRoleImportSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ServiceRoleImportSheet({ open, onOpenChange }: ServiceRoleImportSheetProps) {
  const queryClient = useQueryClient();

  return (
    <ImportSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Import service roles"
      description="Upload an .xlsx or .csv."
      onImport={async (file, { idempotencyKey }) => {
        const res = await serviceRolesApi.importFile(file, { idempotencyKey });
        return {
          totalRows: res.data.totalRows,
          created: res.data.created,
          updated: res.data.updated,
          failed: res.data.failed,
          rows: res.data.rows.map((r) => ({
            rowNumber: r.rowNumber,
            outcome: r.outcome,
            // Pass structured errors straight through — the generic
            // ImportSheet renders `{ code, message, field }` objects
            // with a field chip next to each message.
            errors: r.errors,
          })),
        };
      }}
      onSuccess={() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.serviceRoles.all });
      }}
      onDownloadTemplate={async () => {
        try {
          const { blob, filename } = await serviceRolesApi.exportTemplate();
          triggerDownload(blob, filename ?? 'service-roles-template.xlsx');
        } catch (err) {
          const msg = err instanceof ApiError ? err.message : 'Template download failed';
          toast.error(msg);
        }
      }}
    />
  );
}
